import React, { useRef } from "react";
import { motion, useMotionTemplate, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { LocalClock } from "../LocalClock";
import { Logo } from "../Logo";
import { media } from "../../data/media";

/** Positioning facts, kept as data so the rail and the heading never drift apart. */
const facts = ["10+ Years", "Worldwide", "Motion", "VFX", "CGI", "AI"];
export function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });
  const exitOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.15]);
  const exitScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const exitY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const smoothX = useSpring(pointerX, {
    stiffness: 55,
    damping: 22
  });
  const smoothY = useSpring(pointerY, {
    stiffness: 55,
    damping: 22
  });
  const plateX = useTransform(smoothX, [-0.5, 0.5], [-10, 10]);
  const plateY = useTransform(smoothY, [-0.5, 0.5], [-7, 7]);
  const typeX = useTransform(smoothX, [-0.5, 0.5], [6, -6]);

  // Matches the contact section's practical light so both bookend sections read the same.
  const lightX = useMotionValue(50);
  const lightY = useMotionValue(40);
  const smoothLightX = useSpring(lightX, {
    stiffness: 60,
    damping: 24
  });
  const smoothLightY = useSpring(lightY, {
    stiffness: 60,
    damping: 24
  });
  const spotlight = useMotionTemplate`radial-gradient(circle at ${smoothLightX}% ${smoothLightY}%, rgba(84,246,198,0.16), transparent 55%)`;
  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType !== "mouse" || reduceMotion) return;
    const rect = event.currentTarget.getBoundingClientRect();
    pointerX.set((event.clientX - rect.left) / rect.width - 0.5);
    pointerY.set((event.clientY - rect.top) / rect.height - 0.5);
    lightX.set((event.clientX - rect.left) / rect.width * 100);
    lightY.set((event.clientY - rect.top) / rect.height * 100);
  };
  return <section ref={sectionRef} onPointerMove={onPointerMove} aria-labelledby="hero-heading" className="relative flex min-h-[640px] flex-col justify-between overflow-hidden px-5 pb-8 pt-24 sm:min-h-[760px] md:min-h-[100svh] md:px-8 md:pb-10" data-id="element-149" data-mp="3ouh7brrxu1jr">
      <motion.div style={{
      x: plateX,
      y: plateY,
      scale: reduceMotion ? 1 : exitScale
    }} className="absolute inset-[-24px]" data-id="element-150" data-mp="2w4ibndj8n664">
        <img src={media.ctaPlate} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-50" data-id="element-151" data-mp="363zgjsqo1g11" />
      </motion.div>
      <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(5,8,8,0.97),rgba(5,8,8,0.6))]" data-id="element-152" data-mp="3aw0y9u6lryyg" />
      {!reduceMotion && <motion.div aria-hidden="true" className="absolute inset-0" style={{
      background: spotlight
    }} data-id="element-153" data-mp="2dfli5bvo0hoy" />}
      <div className="absolute inset-0 film-grain opacity-20" data-id="element-154" data-mp="2ictzxbcwijpo" />

      <div aria-hidden="true" className="pointer-events-none absolute inset-4 hidden md:block" data-id="element-155" data-mp="ziskelkwyat0">
        <span className="absolute left-0 top-0 h-8 w-px bg-white/25" data-id="element-156" data-mp="vja1fvcnk9st" /><span className="absolute left-0 top-0 h-px w-8 bg-white/25" data-id="element-157" data-mp="qj5k633x7b1j" />
        <span className="absolute right-0 top-0 h-8 w-px bg-white/25" data-id="element-158" data-mp="ql8h773hq4fr" /><span className="absolute right-0 top-0 h-px w-8 bg-white/25" data-id="element-159" data-mp="lizma5724sb0" />
        <span className="absolute bottom-0 left-0 h-8 w-px bg-white/25" data-id="element-160" data-mp="gkprhvp8vtpx" /><span className="absolute bottom-0 left-0 h-px w-8 bg-white/25" data-id="element-161" data-mp="gn2faphooyaw" />
        <span className="absolute bottom-0 right-0 h-8 w-px bg-white/25" data-id="element-162" data-mp="btg471r652h7" /><span className="absolute bottom-0 right-0 h-px w-8 bg-white/25" data-id="element-163" data-mp="7s4llg6pphii" />
      </div>

      <div className="relative mx-auto flex w-full max-w-[1480px] items-center justify-between gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50" data-id="element-164" data-mp="2tt9eysy2ux4">
        <span className="flex items-center gap-3" data-id="element-165" data-mp="3u1yr3x2711mq">
          <Logo className="h-[18px] w-[40px]" data-id="element-166" data-mp="3p0htcaf09vad" />
          <span aria-hidden="true" className="hidden h-px w-8 bg-white/25 sm:block" data-id="element-167" data-mp="3p2uj39r099xz" />
          <span className="text-white/75" data-id="element-168" data-mp="1syeptzu0fvzh">001</span>
          SM Zeeshan
        </span>
        <span className="flex items-center gap-4" data-id="element-169" data-mp="1xvemxl8vtoeg">
          <LocalClock className="hidden sm:inline" data-id="element-170" data-mp="1j8391cp89sb6" />
          <span className="flex items-center gap-2 text-white/65" data-id="element-171" data-mp="1j7aa2xwig73l"><span className="h-1.5 w-1.5 rounded-full bg-[#ff5f56] rec-pulse" data-id="element-172" data-mp="1o6eivnfiw2te" />REC</span>
        </span>
      </div>

      <motion.div style={reduceMotion ? undefined : {
      opacity: exitOpacity,
      y: exitY
    }} className="relative mx-auto flex w-full max-w-[1480px] flex-col items-center py-12 text-center" data-id="element-173" data-mp="1a6vlnpo92bvv">
        <motion.p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.28em] text-[#54f6c6] sm:text-xs" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.45,
        duration: 0.6
      }} data-id="element-174" data-mp="10a0pvejd2pla">
          <span data-id="element-175" data-mp="2zla8g4fpjooq">Senior 2D &amp; 3D Motion Designer</span>
          <span aria-hidden="true" className="text-[#54f6c6]/45" data-id="element-176" data-mp="ny6mojry8dsp">|</span>
          <span data-id="element-177" data-mp="izn32q0tve3i">VFX ARTIST | CGI GENERALIST</span>
        </motion.p>

        <motion.h1 id="hero-heading" style={reduceMotion ? undefined : {
        x: typeX
      }} className="mt-7 sm:mt-9" data-id="element-178" data-mp="j1gboav048e5">
          <span className="sr-only" data-id="element-179" data-mp="xqm8xtaa3oje">Syed Muhammad Zeeshan, Senior 2D &amp; 3D Motion Designer and VFX Artist</span>

          <span aria-hidden="true" className="block overflow-hidden" data-id="element-180" data-mp="16p7n7c8pwhed">
            <motion.span className="block text-[clamp(1.55rem,7.4vw,2.6rem)] font-light leading-[1.04] tracking-[-0.01em] text-[#f4f6f1]/85 sm:text-[clamp(2.1rem,6.4vw,3.9rem)] lg:text-[clamp(2.6rem,4.6vw,4.6rem)]" initial={{
            y: "110%"
          }} animate={{
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.12,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-181" data-mp="16nefdtmdbr4s">
              Syed Muhammad
            </motion.span>
          </span>

          <span aria-hidden="true" className="mt-1 block overflow-hidden sm:mt-2" data-id="element-182" data-mp="3dgp8lrblfk0n">
            <motion.span className="block text-[clamp(3.4rem,17vw,6rem)] font-semibold leading-[0.92] tracking-[-0.055em] text-[#f4f6f1] sm:text-[clamp(4.5rem,15vw,9rem)] lg:text-[clamp(6rem,11vw,11rem)]" initial={{
            y: "110%"
          }} animate={{
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.22,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-183" data-mp="1hd2ael47374g">
              Zeeshan
            </motion.span>
          </span>
        </motion.h1>

        <motion.div className="mt-8 flex flex-col items-center sm:mt-10" initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7,
        duration: 0.7
      }} data-id="element-184" data-mp="209kapqx40fcb">
          <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.2em] text-white/55 sm:text-xs" data-id="element-185" data-mp="2e3bai7l1gkuu">
            {facts.map((fact, index) => <React.Fragment key={fact} data-id="element-186" data-mp="2o069ssp4ypa6" data-mpi={index}>
                {index > 0 && <span aria-hidden="true" className="text-white/25" data-id="element-187" data-mp="2nymryxnu066a">|</span>}
                <span data-id="element-188" data-mp="2y6h90vy8nqfy">{fact}</span>
              </React.Fragment>)}
          </p>
        </motion.div>
      </motion.div>

      <div className="relative mx-auto w-full max-w-[1480px]" data-id="element-189" data-mp="2t85wuekh13uk">
        <div className="flex items-center justify-between gap-6 border-t border-white/15 pt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45" data-id="element-190" data-mp="2o78i6byo6rxt">
          <span className="truncate" data-id="element-191" data-mp="3gvxb3npknqyh">Creative direction · CGI · Compositing · Post</span>
          <span className="flex shrink-0 items-center gap-3" data-id="element-192" data-mp="3bwksjf5qh61p">Scroll<span className="h-8 w-px bg-white/25 scroll-hint" data-id="element-193" data-mp="3bx2u8rb60ch0" /></span>
        </div>
      </div>
    </section>;
}